"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Cpu, Activity, ShieldCheck, Download, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Superflux() {
  const shouldReduceMotion = useReducedMotion();
  const specs = [{
    label: "Technology",
    value: "Bruker PAT Matrix"
  }, {
    label: "Deployment",
    value: "Inline / At-line Process"
  }, {
    label: "Compliance",
    value: "21 CFR Part 11 Ready"
  }, {
    label: "Response Time",
    value: "Application dependent"
  }];
  const highlights = ["High-throughput real-time multi-channel monitoring", "Direct integration with industrial DCS and SCADA architectures", "Full qualification (IQ/OQ/PQ) and local lifecycle support by Antech"];
  return <section id="herosuperflux" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Background Decorative Grid */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(19,89,54,0.06),transparent)] dark:bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(19,89,54,0.12),transparent)]" />
      
      <div className="page-container relative">
        {/* Breadcrumb / Top Tag */}
        <div className="mb-6 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Link to="/HomePage" className="transition-colors hover:text-foreground">
            Home
          </Link>
          <ChevronRight className="size-3 text-muted-foreground/60" />
          <Link to="/ProcessAnalyticalTechnologyPage" className="transition-colors hover:text-foreground">
            Process Analytical Technology
          </Link>
          <ChevronRight className="size-3 text-muted-foreground/60" />
          <span className="font-medium text-foreground">SuperFlux</span>
        </div>

        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Product Information & CTAs */}
          <div className="flex flex-col items-start lg:col-span-7">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary px-3 py-1 text-xs font-semibold">
                Bruker PAT Family
              </Badge>
              <Badge variant="secondary" className="gap-1 px-3 py-1 text-xs">
                <ShieldCheck className="size-3 text-primary" /> Regulated Manufacturing Ready
              </Badge>
            </div>

            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-[1.15] mt-4">
              SuperFlux High-Performance Analytical Solution
            </h1>

            <p className="font-default mt-5 text-base text-muted-foreground sm:text-lg leading-relaxed max-w-2xl">
              Next-generation Process Analytical Technology (PAT) engineered for continuous inline monitoring, blend uniformity, and critical quality attribute analysis across regulated pharmaceutical and chemical manufacturing.
            </p>

            {/* Feature Highlights List */}
            <ul className="mt-6 space-y-2.5">
              {highlights.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>)}
            </ul>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3.5 sm:gap-4">
              <Button asChild size="lg" className="rounded-md px-6">
                <Link to="/BookADemoPage">
                  Book a Demonstration
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md px-6">
                <Link to="/DiscussYourApplicationPage">
                  Consult an Specialist
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="rounded-md px-4">
                <Link to="/ResourcesPage">
                  <Download className="size-4" />
                  Technical Datasheet
                </Link>
              </Button>
            </div>

            {/* Key Specs Row */}
            <div className="mt-10 grid w-full grid-cols-2 gap-4 border-t border-border pt-6 sm:grid-cols-4">
              {specs.map((spec, i) => <div key={i} data-index={i} className="flex flex-col">
                  <span className="text-xs text-muted-foreground">{spec.label}</span>
                  <span className="mt-1 text-xs sm:text-sm font-semibold text-foreground">{spec.value}</span>
                </div>)}
            </div>
          </div>

          {/* Right Column: Interactive Hardware Visual & Telemetry Card */}
          <div className="relative lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="relative mx-auto w-full max-w-lg rounded-2xl border border-border bg-card p-4 shadow-xl sm:p-6">
              {/* Product Visual Container */}
              <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/superflux_analyzer_pat_system.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/superflux_analyzer_pat_system.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/superflux_analyzer_pat_system.png-wvc-sizes" alt="SuperFlux Bruker PAT Process Analytical System" loading="eager" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-background/90 backdrop-blur-xs px-2.5 py-1 text-xs font-medium text-foreground shadow-xs border border-border/60">
                    <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                    Online Monitoring
                  </span>
                </div>
              </div>

              {/* Status and Capability Matrix */}
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-secondary/70 p-3 text-secondary-foreground border border-border/40">
                  <div className="flex items-center gap-2.5">
                    <div className="flex size-8 items-center justify-center rounded-md bg-background text-primary shadow-2xs">
                      <Cpu className="size-4" />
                    </div>
                    <div>
                      <p className="text-xs font-medium">Spectral Engine</p>
                      <p className="text-xs text-muted-foreground">High Sensitivity Optics</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[11px] bg-background">
                    Active
                  </Badge>
                </div>

                <div className="flex items-center justify-between rounded-lg bg-secondary/70 p-3 text-secondary-foreground border border-border/40">
                  <div className="flex items-center gap-2.5">
                    <div className="flex size-8 items-center justify-center rounded-md bg-background text-primary shadow-2xs">
                      <Activity className="size-4" />
                    </div>
                    <div>
                      <p className="text-xs font-medium">Data Acquisition</p>
                      <p className="text-xs text-muted-foreground">Automated Endpoint Detection</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[11px] bg-background">
                    Calibrated
                  </Badge>
                </div>
              </div>

              {/* Bottom Support Guarantee Note */}
              <div className="mt-4 flex items-center justify-between border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
                <span>Distributed & Supported by Antech Solutions</span>
                <span className="font-semibold text-foreground">Ireland & UK</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}